import {
  createContext,
  useContext,
  useId,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { cx } from './internal/cx.js';

interface TabsContextValue {
  value: string;
  setValue: (value: string) => void;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tab components must be used within Tabs.Root');
  return ctx;
}

function getEnabledTabs(list: HTMLElement | null): HTMLElement[] {
  if (!list) return [];
  return Array.from(list.querySelectorAll<HTMLElement>('[role="tab"]:not(:disabled)'));
}

export interface TabsRootProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}

function TabsRoot({
  children,
  className,
  value: valueProp,
  defaultValue = '',
  onValueChange,
  ...props
}: TabsRootProps): JSX.Element {
  const baseId = useId();
  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;

  function setValue(next: string): void {
    if (!isControlled) setInternalValue(next);
    onValueChange?.(next);
  }

  return (
    <TabsContext.Provider value={{ value, setValue, baseId }}>
      <div className={cx('nrx-tabs', className)} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export interface TabsListProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  'aria-label'?: string;
}

function TabsList({
  children,
  className,
  'aria-label': ariaLabel,
  ...props
}: TabsListProps): JSX.Element {
  const { setValue } = useTabsContext();
  const listRef = useRef<HTMLDivElement>(null);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const tabs = getEnabledTabs(listRef.current);
    if (tabs.length === 0) return;

    const currentIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      event.preventDefault();
      nextIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    const nextTab = tabs[nextIndex];
    if (!nextTab) return;

    const nextValue = nextTab.dataset['tabValue'];
    if (nextValue) {
      setValue(nextValue);
      nextTab.focus();
    }
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cx('nrx-tabs__list', className)}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
    </div>
  );
}

export interface TabProps extends ComponentPropsWithoutRef<'button'> {
  children: ReactNode;
  value: string;
}

function Tab({ children, className, value, disabled, ...props }: TabProps): JSX.Element {
  const { value: activeValue, setValue, baseId } = useTabsContext();
  const isSelected = activeValue === value;
  const tabId = `${baseId}-tab-${value}`;
  const panelId = `${baseId}-panel-${value}`;

  return (
    <button
      type="button"
      role="tab"
      id={tabId}
      data-tab-value={value}
      aria-selected={isSelected}
      aria-controls={panelId}
      tabIndex={isSelected ? 0 : -1}
      disabled={disabled}
      className={cx('nrx-tabs__tab', isSelected && 'nrx-tabs__tab--selected', className)}
      onClick={() => setValue(value)}
      {...props}
    >
      {children}
    </button>
  );
}

export interface TabPanelProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  value: string;
}

function TabPanel({ children, className, value, ...props }: TabPanelProps): JSX.Element {
  const { value: activeValue, baseId } = useTabsContext();
  const isSelected = activeValue === value;
  const tabId = `${baseId}-tab-${value}`;
  const panelId = `${baseId}-panel-${value}`;

  if (!isSelected) return <></>;

  return (
    <div
      role="tabpanel"
      id={panelId}
      aria-labelledby={tabId}
      className={cx('nrx-tabs__panel', className)}
      {...props}
    >
      {children}
    </div>
  );
}

/** Tabbed panel navigation for sidebars and settings. */
export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Tab,
  Panel: TabPanel,
};
